import { AppContext } from "App/index";
import { DeviceInfo } from "Device/Info/DeviceInfo";
import { DIContainer } from "Global/Base/DIContainer";
import { UUID } from "Global/Base/IDs/UUID";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "Global/Base/Rights/RightTypes";
import { SetUtils } from "Global/Base/Utils/SetUtils";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { EOWFormDefinitionNotFoundException, EOWNoFormNameException } from "OW.Remoting/Basic/OWExceptions";
import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { ProfileChangeType } from "UI/UI.React/types/multiweb/index";
import { FormGroupContainer } from "UI/UI/DefaultInterface/FormGroupContainer";
import { Event } from "../../../../Global/Base/Events";
import { EventsController } from "../../../../Global/Dom/EventsController";
import { BreadcrumbNavItem } from "../BreadcrumbNavItem";
import { IFRMParams } from "../Controls/FormParameters";
import { FormUICustomProperty, IFormGroupMenu, IFormMenu, IFormUI } from "../FormUI/FormUI";
import { IBreadcrumbNav } from "../IBreadcrumbNav";
import { UIManager } from "../UIManager";
import { IWindowable, WindowState } from "../UIManagerInterfaces";
import { Form } from "../XGControls/Form";
import { IForm, IUsingFormUI } from "../XGControls/IForm";
import { ModalWindow, ModalWindowType } from "../XGControls/ModalWindow";
import { IShowModalParams } from "../XGControls/ModalWindowInterfaces";
import { FormEvent, FormEventVisibilityChanged, IFormGroup, IFormGroupOpenModalParams } from "./IFormGroup";
import { FormManager } from "./IFormManager";
import { FormManagerProperty } from "./IFormManagerInterfaces";

/** Třída sloužící k obsluze jedné skupiny formulářů. */
export class FormGroup implements IFormGroup {
    public readonly formOpened: Event<IFormGroup, FormEvent> = new Event<IFormGroup, FormEvent>();
    public readonly formClosed: Event<IFormGroup, FormEvent> = new Event<IFormGroup, FormEvent>();
    public readonly formGroupVisibilityChanged: Event<IFormGroup, FormEventVisibilityChanged> = new Event<IFormGroup, FormEventVisibilityChanged>();
    public readonly formVisibilityChanged: Event<IFormGroup, FormEventVisibilityChanged> = new Event;

    protected _formName: string;
    protected readonly _formInstanceId: string;
    protected _form: IWindowable;
    protected _subForms: Set<ModalWindow> = new Set<ModalWindow>();
    protected _container: FormGroupContainer;
    protected _breadcrumbs: IBreadcrumbNav;
    protected _formMenu: IFormMenu;
    protected _openedParams: IFRMParams;
    protected _modalParentContainer: HTMLElement;
    protected _modalWindowsContainer: HTMLElement;

    protected _isPlaced: boolean = false;

    protected _formGroupContainer: IFormGroupMenu | undefined;
    protected _modalWindowCounter: number = 0;
    protected static _mainFormOpenedCount: number = 0;

    protected _eventsController = new EventsController();

    public get formName(): string {
        return this._formName;
    }
    public get form(): IWindowable {
        return this._form;
    }
    public get container(): FormGroupContainer {
        return this._container;
    }
    public get currentModal(): ModalWindow {
        return SetUtils.last(this._subForms);
    }
    public get formInstanceId(): string {
        return this._formInstanceId;
    }

    private constructor(formInstanceId: string) {
        this._formInstanceId = formInstanceId;
        this._modalParentContainer = document.getElementById("modal-windows-parent");
    }

    public static async initialize(formInstanceId: string, options?: FormGroupParams): Promise<FormGroup> {
        const fmi = new FormGroup(formInstanceId);

        if (!options) return fmi;

        if ("form" in options) {
            fmi._form = options.form;
            if (options.form instanceof IForm) fmi._formName = options.form.formName;
        } else {
            fmi._formName = options.formName;
        }
        fmi._container = options.container;

        fmi._breadcrumbs = await options?.breadcrumbs?.();

        if (!fmi._breadcrumbs) {
            fmi._breadcrumbs = DIContainer.instance.create(IBreadcrumbNav);
            fmi._breadcrumbs.initialize();
        }

        fmi._formMenu = await options?.formMenu?.() ?? undefined;
        fmi._modalWindowsContainer = createElement({ classes: "modal-group-container hidden" });

        fmi._modalParentContainer.appendChild(fmi._modalWindowsContainer);

        fmi._openedParams = options.params;

        if ("setFormUI" in options && options.setFormUI) {
            await fmi._afterFormOpen(options.form);
        }

        await fmi.openMainForm(true);

        fmi.container.formGroup = fmi;

        return fmi;
    }

    /**
     * Otevře podřízený formulář
     * @param param
     */
    public async openForm(param: IFormGroupOpenModalParams): Promise<IForm> {
        if (!param.parametricData) param.parametricData = {};
        param.parametricData.isModal = true;

        let form: IForm;

        if ("form" in param) {
            if (!(param.form instanceof IForm)) throw Error("Only support IForm");
            form = param.form;
        } else {
            if (!param.formName) {
                throw new EOWNoFormNameException("");
            }
            form = await this._openForm(param.formName, this.formInstanceId, param.parametricData, this._form);
        }

        this._showModal({ window: form, type: param.type, onClose: param.onClose, showOverlay: false, showBreadcrumbNav: param.showBreadcrumbNav == undefined ? true : param.showBreadcrumbNav });

        form.setState(WindowState.active, true);
        form.setState(WindowState.visible, true);

        return form;
    }

    /**
     * Otevře vlastní formulář
     *
     * @param form Formulář, který chceme otevřít
     * @param init Nepovinná inizializační metoda, které je předáné aktuální IFormUI
     */
    public async openCustomModalForm<T extends IWindowable>(form: T): Promise<T> {
        await this._afterFormOpen(form);

        form.getElement()?.setAttribute("data-form-id", this._getPrefix());

        return form;
    }

    private async _showModal(config: IShowModalParams): Promise<void> {
        if (!config.modalConfig) {
            switch (config.type) {
                case ModalWindowType.lov:
                case ModalWindowType.modalForm:
                case ModalWindowType.parametricForm:
                case ModalWindowType.errorMsg:
                {
                    config.modalConfig = {
                        showFooter: false,
                        showHeader: true,
                        resizeable: false,
                        closeAble: false,

                    };
                    break;
                }
            }
        }

        const modalWindow = new ModalWindow(config);

        this.form.setState(WindowState.previewActive, false);
        for (const subForm of this._subForms) {
            const form = subForm.window as IForm;
            form.setState(WindowState.previewActive, false);
            //form.formUI.formMenu.setProperty(FormUICustomProperty.formMenuVisibility, false);
        }

        this._subForms.add(modalWindow);
        const formUuid = UUID.generate();
        this._eventsController.addWithKey(formUuid, config.window.closed, () => {
            this._subForms.delete(modalWindow);
            this.formClosed.trigger(this, {
                form: config.window as IForm,
                parentForm: this._form,
            });

            if (this._subForms.size == 0) {
                this._modalWindowsContainer.classList.add("hidden");
                this._modalParentContainer.classList.add("hidden");
            }
            this._eventsController.removeByKey(formUuid);
        });

        let item: BreadcrumbNavItem = null;

        if (config.showBreadcrumbNav && (DeviceInfo.deviceProperties.desktop || DeviceInfo.deviceProperties.tablet)) {
            item = this._addBreadcrumbs(config.window as IForm);
        } else {
            this._loadDataWithoutBreadcrumbs();
        }

        if (config.window instanceof IForm) {
            config.window.formUI.formMenu.setProperty(FormUICustomProperty.formMenuVisibility, true);
            config.window.setState(WindowState.visible, true);
        }

        this._modalWindowsContainer.appendChild(modalWindow.container);
        const openInBackground = !this._container.visible; // Pokud se přeplo na jiný formulář před otevřením modálního okna
        if (!openInBackground) {
            this._modalWindowsContainer.classList.remove("hidden");
            this._modalParentContainer.classList.remove("hidden");

            if (config.showOverlay)
                UIManager.instance.showOverlay(true, false); //TODO: az se sem bude pridavat druhy parametr true, tak smazat width 100% u #loader
        }

        if (item)
            item.onSelectedEvent.trigger(item, item);

        this.formOpened.trigger(this, {
            form: config.window as IForm,
            parentForm: this._form,
        });
    }

    private _addBreadcrumbs(form: IWindowable): BreadcrumbNavItem {
        return this._breadcrumbs?.addNavigationItem(form);
    }

    public hide(): void {
        this.container.visible = false;
        this._breadcrumbs.hide();
        if (this.form instanceof IForm) {
            this.form.formUI.formGroupMenu.hidePageMenu = true;
            this.form.formUI.formMenu?.setProperty(FormUICustomProperty.formMenuVisibility, false);
        }

        if (this.currentModal) {
            if (this.currentModal.window instanceof IForm) this.currentModal.window.setState(WindowState.visible, false);
        } else this.form.setState(WindowState.visible, false);

        if (this._subForms.size > 0) {
            this._modalWindowsContainer.classList.add("hidden");
            this._modalParentContainer.classList.add("hidden");
        }

        this.formVisibilityChanged.trigger(this, { form: this.form, isVisible: false, parentForm: null });
    }

    public show(init: boolean = false): void {
        if (!this._isPlaced) {
            this._container.appendChild(this._form.getElement());

            if ((DeviceInfo.deviceProperties.desktop || DeviceInfo.deviceProperties.tablet) /*&& this._form instanceof IForm*/) {
                this._addBreadcrumbs(this._form);
            }
            this._isPlaced = true;
        } else {
            this.container.visible = true;
            this._breadcrumbs.show();
        }

        if (this.form instanceof IForm) {
            //Form UI nemusí být ještě zinicializované.
            if (!this.form.formUI) {
                this.form.framesInitialized.add(() => this._initializeIForm(this.form as IForm), true);
            } else {
                this._initializeIForm(this.form);
            }
        }

        // Nezobrazovat formulář při inicializaci pro možnost otevření na pozadí
        if (init)
            return;

        const items = this._breadcrumbs?.items;
        if (items?.length) {
            const lastItem = items[items.length - 1];
            lastItem.onSelectedEvent.trigger(lastItem, lastItem);
        } else {
            this._loadDataWithoutBreadcrumbs();
        }

        if (this._subForms.size > 0) {
            this._modalWindowsContainer.classList.remove("hidden");
            this._modalParentContainer.classList.remove("hidden");
        }

        if (this.currentModal) {
            if (this.currentModal.window instanceof IForm) this.currentModal.window.setState(WindowState.visible, true);
        } else this.form.setState(WindowState.visible, true);

        this.formVisibilityChanged.trigger(this, { form: this.form, isVisible: true, parentForm: null });
    }

    private _loadDataWithoutBreadcrumbs(): void {
        UIManager.instance.showLoader();
        try {
            this._form.setState(WindowState.active, true);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _initializeIForm(form: IForm): void {
        form.formUI.formGroupMenu.hidePageMenu = false;
        if (!this.currentModal)
            form.formUI?.formMenu?.setProperty(FormUICustomProperty.formMenuVisibility, true);
    }

    public async close(): Promise<void> {
        this._formGroupContainer.hidePageMenu = true;
        this.formClosed.trigger(this, {
            form: this._form,
            parentForm: null,
        });

        this.dispose();
    }

    public async openMainForm(init: boolean = false): Promise<void> {
        if (!this._form) this._form = await this._openForm(this.formName, this.formInstanceId, this._openedParams, null);
        const form = this._form;

        this.show(init);

        this.formOpened.trigger(this, {
            form: form,
            parentForm: null,
        });

        const formUuid = UUID.generate();

        if (form instanceof IForm) {
            this._eventsController.addWithKey(formUuid, form.stateChanged, (_sender, param) => {
                if (param.state == WindowState.active) {
                    if (param.value) this.show();
                    else this.hide();
                }
            });

            this._eventsController.addWithKey(formUuid, FormManager.instance.favoriteFormSet, (_formManager, { formCode, isFavorite, profile }) => {
                if (formCode === this.formName && form.profileManager?.selectedProfile?.id === profile?.id) {
                    form.setState(WindowState.favorite, isFavorite);
                }
            });

            this._eventsController.addWithKey(formUuid, form.closed, async (sender) => {
                if (sender instanceof Form) await sender.waitForPendingActions();
                this.close();
                if (sender instanceof IForm) {
                    this._eventsController.removeByKey(formUuid);
                }
            });
        }

        if (form instanceof Form) {
            this._eventsController.addWithKey(formUuid, form.profileManager.profileAdded, (_manager, profile) => {
                FormManager.instance.formProfileChanged.trigger(FormManager.instance, { type: ProfileChangeType.add, profile: profile });
            });
            this._eventsController.addWithKey(formUuid, form.profileManager.profileUpdated, (_manager, profile) => {
                FormManager.instance.formProfileChanged.trigger(FormManager.instance, { type: ProfileChangeType.update, profile: profile });
            });
            // TODO: pridat nasledne profileEdited
            this._eventsController.addWithKey(formUuid, form.profileManager.profileRemoved, (_manager, profile) => {
                FormManager.instance.formProfileChanged.trigger(FormManager.instance, { type: ProfileChangeType.remove, profile: profile });
                // Z historie otevřených položek odstraní právě smazaný profil.
                FormManager.instance.openedFormsHistoryManager.markOpenedFormsHistoryItemToBeRemovedOnFormClose(form, undefined, profile.id);
            });
        }

        //if (topUIManager._mainMenu)
        //    topUIManager._mainMenu.registerOpenedWindow(form);

        if (form instanceof IForm && this._openedParams?.menuItemId)
            form.setState(WindowState.favorite, await FormManager.instance.getProperty(FormManagerProperty.favoriteForm, { menuId: this._openedParams.menuItemId, formCode: form.formName }));

        if (!init)
            form.setState(WindowState.active, true);

        if (form instanceof IForm && !form.canClose)
            form.setState(WindowState.closeDisabled, true);
    }

    private _adjustModalWindowIds(root: HTMLElement): HTMLElement {
        const elements = root.querySelectorAll("[id]");
        const prefix = this._getPrefix();
        const combinedPrefix = `${prefix}_`;
        const rootId = root.id?.toLowerCase();

        root.setAttribute("data-form-id", prefix);
        root.id = rootId ? combinedPrefix + rootId : prefix;

        for (const e of elements) {
            e.id = combinedPrefix + e.id;
        }

        return root;
    }

    private _getPrefix(): string {
        return `frm${++FormGroup._mainFormOpenedCount}`;
    }

    protected async _openForm(formName: string, formInstanceId: string, parametricData: IFRMParams, parentForm?: IWindowable): Promise<IForm> {
        if (parentForm != null && !(parentForm instanceof IForm)) {
            throw Error("Argument parentForm have to be null or instance of IForm");
        }

        const formDef = await UIManager.instance.loadFormDefinition(formName);

        if (!formDef) {
            const ex2 = new EOWFormDefinitionNotFoundException("");
            ex2.FormName = formName;
            throw ex2;
        }

        FormGroup._loadLocalyEvaluableFormConditions(formName); // synchronně načtě funkce pro lokální vyhodnocování podmínek a vloží je do html hlavičky (musí být nachystáno před Form.init())

        const element = this._adjustModalWindowIds(formDef);

        // Formuláře mají vlastní práva - v "data-tscitem="FormRight" řešená odlišně.
        const originalRightsDefinition = EMPTY_ITEM_RIGHTS_COLLECTION;

        let modifiedFormInstanceId = formInstanceId;

        if (formInstanceId && parametricData.isModal) {
            this._modalWindowCounter++;
            modifiedFormInstanceId += `${"-Modal:" + this._modalWindowCounter}`;
        }

        // TODO:[IFRAME] Odstranit cyklickou vazbu mezi FormUI a Formem
        const form = new Form({
            ownerForm: parentForm as IForm,
            element,
            originalRightsDefinition,
            formInstanceId: modifiedFormInstanceId,
            parametricData,
        });

        await this._afterFormOpen(form);

        return form;
    }

    private async _afterFormOpen<F extends IWindowable>(f: F): Promise<void> {
        if (!this._formGroupContainer) {
            this._formGroupContainer = DIContainer.instance.create(IFormGroupMenu);

            if (this._breadcrumbs)
                this._formGroupContainer.setProperty(FormUICustomProperty.breadcrumbsElement, this._breadcrumbs);
        }

        const formUI = DIContainer.instance.create(IFormUI, f);
        formUI.formGroupMenu = this._formGroupContainer;

        if (f instanceof IForm) await f.init(formUI);
        else if (f instanceof IUsingFormUI) f.formUI = formUI;
    }

    protected static _loadLocalyEvaluableFormConditions(formName: string): void {
        formName = formName?.toLowerCase();
        const script = document.createElement("script");
        const handlerUrlPart = isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03")
            ? `/mwclient/js/${formName}.aspx`
            : `/api/v01/as/gui/forms/${formName}/`;
        script.src = `${AppContext.instance.addresses.serviceUrl}${handlerUrlPart}?clientType=MultiWeb&getOnlyConditions=true`;
        document.head.insertAdjacentElement("beforeend", script);
    }

    public dispose(): void {
        this.formOpened.removeAll();
        this.formClosed.removeAll();
        this.formGroupVisibilityChanged.removeAll();
        this.formVisibilityChanged.removeAll();

        this._subForms?.forEach((modal) => modal.dispose());
        this._subForms?.clear();
        this._container?.remove();
        this._breadcrumbs?.dispose();
        this._modalWindowsContainer?.remove();
        this._formGroupContainer?.dispose();
        this._eventsController.dispose();
    }
}

interface FormGroupParamsInner {
    breadcrumbs?: () => Promise<IBreadcrumbNav>;
    formMenu?: () => Promise<IFormMenu>;
    container: FormGroupContainer;
    params?: IFRMParams;
}

interface FormGroupParamsForm extends FormGroupParamsInner {
    form: IWindowable;
    setFormUI?: boolean;
}

interface FormGroupParamsFormName extends FormGroupParamsInner {
    formName: string;
}

export type FormGroupParams = FormGroupParamsForm | FormGroupParamsFormName;
